// Bottom-sheet replacement for native <select>.
// Renders a pill-button showing the current option's label, opens a
// rose-pink modal sheet with full-width options on click.
//
// API mirrors a controlled select: value + onChange + options. Disabled
// state supported.

import { useEffect, useState, type ReactNode } from "react";

export interface SheetOption<V extends string> {
  value: V;
  label: ReactNode;
  /** Optional grouping label — same group key renders under one header. */
  group?: string;
}

export function SelectSheet<V extends string>({
  value,
  onChange,
  options,
  placeholder,
  title,
  disabled,
}: {
  value: V | "";
  onChange: (v: V) => void;
  options: SheetOption<V>[];
  placeholder?: string;
  title?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // Закриваємо при escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const current = options.find((o) => o.value === value);

  // Групуємо опції — якщо хоча б одна має group, рендеримо з заголовками.
  const hasGroups = options.some((o) => o.group);
  const groups = hasGroups
    ? Array.from(
        options.reduce((m, o) => {
          const k = o.group ?? "";
          const arr = m.get(k) ?? [];
          arr.push(o);
          m.set(k, arr);
          return m;
        }, new Map<string, SheetOption<V>[]>()).entries(),
      )
    : [["", options] as [string, SheetOption<V>[]]];

  return (
    <>
      <button
        type="button"
        className="nz-dropdown nz-sheet-trigger"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="nz-dropdown-sel">
          {current?.label ?? (
            <span style={{ opacity: 0.55 }}>
              {placeholder ?? "— Виберіть —"}
            </span>
          )}
        </span>
        <span className="nz-dropdown-caret">▾</span>
      </button>

      {open && (
        <div className="nz-dim" onClick={() => setOpen(false)}>
          <div
            className="nz-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="nz-sheet-grip" />
            {title && <div className="nz-sheet-title">{title}</div>}
            <div className="nz-sheet-list">
              {groups.map(([g, opts]) => (
                <div key={g}>
                  {g && <div className="nz-sheet-group">{g}</div>}
                  {opts.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className="nz-sheet-opt"
                      data-on={o.value === value}
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                    >
                      <span className="nz-sheet-opt-lbl">{o.label}</span>
                      {o.value === value && (
                        <span className="nz-sheet-opt-ck">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <button
              type="button"
              className="nz-sheet-cancel"
              onClick={() => setOpen(false)}
            >
              Скасувати
            </button>
          </div>
        </div>
      )}
    </>
  );
}
